import { CardType } from '@ui-components/ContentList';
import { PlayBadgeSize } from '@ui-components/PlayBadge';
import { getContentSvtId } from '@utils/contentoUtils';
import ContinueWatching from '@utils/models/ContinueWatching';
import { OnItemImpressionType } from '@utils/useTrackImpression';
import AccessServiceTypes from 'app/domain/AccessServiceTypes';
import { Accessibility, ContentItemFragment, Presentation } from 'app/graphql/__generated__/types/contento';
import React from 'react';
import { BannerContainer, CarouselContentItem, CarouselShowcaseCard } from '../ContentItemCarousel.styles';
import { getProgress } from '../helpers';

type Props = {
    items: Array<ContentItemFragment>;
    editModeActivated?: boolean;
    presentation?: Presentation;
    cardType?: CardType;
    preferredAccessVersion?: AccessServiceTypes;
    onItemClick?: (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        itemPosition: number,
        svtId: string,
        autoPlay?: boolean,
        b?: boolean
    ) => void;
    onItemImpression: OnItemImpressionType;
    onMarkItemAsDeleted?: (markedAsDeletedObject: ContinueWatching) => void;
    continueWatchingIsActive?: boolean;
    markedAsDeleted?: Array<ContinueWatching>;
    currentAccessibilityChoiceContento?: Accessibility;
    autoPlay?: boolean;
    playingNowId?: string;
    linkToDetailsPage?: boolean;
    hasHero?: boolean;
    banner?: () => React.ReactNode;
};

const ItemsList = (props: Props): JSX.Element => {
    const {
        items,
        editModeActivated = false,
        presentation,
        preferredAccessVersion,
        onItemClick,
        onItemImpression,
        onMarkItemAsDeleted,
        continueWatchingIsActive,
        markedAsDeleted = [],
        cardType = CardType,
        currentAccessibilityChoiceContento,
        autoPlay,
        playingNowId,
        linkToDetailsPage,
        hasHero = true,
        banner,
    } = props;

    return (
        <>
            {banner && <BannerContainer>{banner()}</BannerContainer>}
            {items.map((content, i) => {
                const id = content.item?.id;
                const svtId = content.item?.svtId;
                if (cardType === CardType.SHOWCASE) {
                    return (
                        <CarouselShowcaseCard
                            key={id}
                            item={content}
                            index={hasHero ? i + 1 : i} // Offset with one due to Hero being in position 1
                            onItemClick={onItemClick}
                            onItemImpression={onItemImpression}
                            badgeSize={PlayBadgeSize.MEDIUM}
                            currentAccessibilityChoiceContento={currentAccessibilityChoiceContento}
                        />
                    );
                }

                const itemIsMarkedAsDeleted = () => {
                    if (markedAsDeleted.length === 0) {
                        return false;
                    }

                    const markedItemIndex = markedAsDeleted.findIndex((item) => item.videoId === svtId);

                    return markedItemIndex > -1;
                };

                const contentItemIndex = i + 1;

                const isPlayingNow = getContentSvtId(content.item, preferredAccessVersion) === playingNowId;

                return (
                    <CarouselContentItem
                        inView
                        index={contentItemIndex}
                        key={id}
                        content={content}
                        listId={id}
                        editModeActivated={editModeActivated}
                        markedAsDeleted={itemIsMarkedAsDeleted()}
                        presentation={presentation}
                        preferredAccessVersion={preferredAccessVersion}
                        progress={getProgress(content, continueWatchingIsActive)}
                        onMarkedAsDeleted={onMarkItemAsDeleted}
                        onClick={onItemClick}
                        onItemImpression={onItemImpression}
                        loadImage
                        itemPosition={contentItemIndex}
                        autoPlay={autoPlay}
                        playingNow={isPlayingNow}
                        linkToDetailsPage={linkToDetailsPage}
                    />
                );
            })}
        </>
    );
};

export default ItemsList;
